/**
 * Legacy v1/v2 normalization and v1→v2 migration helpers.
 * Kept for unmigrated stored access and legacy JWT payloads until all orgs are on v2.
 */
import type {
  iUserAccess,
  iUserAccessV2,
  iUserTokenAccess,
  OrganizationRoleId,
  StoredUserAccess,
  TaDeskCapability,
} from "./access.models";
import {
  deriveCapabilitiesFromRoles,
  isValidUserAccessV1,
  isValidUserAccessV2,
  ORGANIZATION_ROLE_IDS,
} from "./access.models";

export interface NormalizedAccessInput {
  isOwner: boolean;
  capabilities: TaDeskCapability[];
  brandIds: string[];
}

function uniqueCapabilities(caps: TaDeskCapability[]): TaDeskCapability[] {
  return [...new Set(caps)];
}

function normalizeBrandIds(brandIds: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const brandId of brandIds) {
    const trimmed = brandId?.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

export function normalizeUserAccessV1(access: iUserAccess): NormalizedAccessInput {
  if (access.org.isOwner) {
    return { isOwner: true, capabilities: [], brandIds: [] };
  }

  const capabilities = new Set<TaDeskCapability>(access.org.capabilities);
  const brandIds: string[] = [];

  for (const entry of access.brandAccess) {
    const brandId = entry.brandId?.trim();
    if (!brandId) continue;
    brandIds.push(brandId);
    for (const cap of entry.capabilities) {
      capabilities.add(cap);
    }
  }

  return {
    isOwner: false,
    capabilities: uniqueCapabilities([...capabilities]),
    brandIds: normalizeBrandIds(brandIds),
  };
}

export function normalizeUserAccessV2(access: iUserAccessV2): NormalizedAccessInput {
  if (access.org.isOwner) {
    return { isOwner: true, capabilities: [], brandIds: [] };
  }

  const fromRoles = deriveCapabilitiesFromRoles(access.org.roles);
  const capabilities = uniqueCapabilities([
    ...fromRoles,
    ...access.org.capabilities,
  ]);

  return {
    isOwner: false,
    capabilities,
    brandIds: normalizeBrandIds(access.brandIds),
  };
}

export function normalizeStoredUserAccess(
  access: StoredUserAccess
): NormalizedAccessInput {
  if (isValidUserAccessV2(access)) {
    return normalizeUserAccessV2(access);
  }
  return normalizeUserAccessV1(access);
}

const ASSIGNABLE_ORG_ROLE_SET = new Set<string>(ORGANIZATION_ROLE_IDS);

function inferOrgRolesFromCapabilities(
  capabilities: TaDeskCapability[]
): OrganizationRoleId[] {
  const roles: OrganizationRoleId[] = [];
  const caps = new Set(capabilities);
  if (caps.has("VIEW_REPORTS")) roles.push("reporter");
  if (caps.has("VIEW_ACCOUNTING") || caps.has("MANAGE_ACCOUNTING")) {
    roles.push("accountant");
  }
  return roles;
}

/** Convert v1 stored access to v2 (for editor load and migration preview). */
export function adaptAccessV1ToV2(access: iUserAccess): iUserAccessV2 {
  if (access.org.isOwner) {
    return {
      org: { isOwner: true, roles: [], capabilities: [] },
      brandIds: [],
      version: 2,
    };
  }

  const roles = new Set<OrganizationRoleId>();
  for (const entry of access.brandAccess) {
    if (
      entry.roleTemplate &&
      entry.roleTemplate !== "owner" &&
      entry.roleTemplate !== "custom" &&
      ASSIGNABLE_ORG_ROLE_SET.has(entry.roleTemplate)
    ) {
      roles.add(entry.roleTemplate as OrganizationRoleId);
    }
  }
  for (const role of inferOrgRolesFromCapabilities(access.org.capabilities)) {
    roles.add(role);
  }

  const normalized = normalizeUserAccessV1(access);
  const roleList = [...roles];
  const derivedFromRoles = deriveCapabilitiesFromRoles(roleList);

  return {
    org: {
      isOwner: false,
      roles: roleList,
      capabilities: normalized.capabilities.filter(
        (cap) => !derivedFromRoles.includes(cap)
      ),
    },
    brandIds: normalized.brandIds,
    version: 2,
  };
}

export function normalizeTokenAccess(
  tokenAccess: iUserTokenAccess
): NormalizedAccessInput {
  if (tokenAccess.isOwner) {
    return { isOwner: true, capabilities: [], brandIds: [] };
  }

  if (Array.isArray(tokenAccess.capabilities)) {
    return {
      isOwner: false,
      capabilities: uniqueCapabilities([...tokenAccess.capabilities]),
      brandIds: normalizeBrandIds(tokenAccess.brandIds ?? []),
    };
  }

  const legacyOrgCaps = tokenAccess.orgCaps ?? [];
  const legacyBrands = tokenAccess.brands ?? {};
  const capabilities = new Set<TaDeskCapability>(legacyOrgCaps);
  const brandIds: string[] = [];

  for (const [brandId, caps] of Object.entries(legacyBrands)) {
    const trimmed = brandId?.trim();
    if (!trimmed) continue;
    brandIds.push(trimmed);
    for (const cap of caps) {
      if (cap) capabilities.add(cap);
    }
  }

  return {
    isOwner: false,
    capabilities: uniqueCapabilities([...capabilities]),
    brandIds: normalizeBrandIds(brandIds),
  };
}
